import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { CalendarBlank } from '@phosphor-icons/react';
import { useAuth } from '@/core/AuthContext';
import { functions } from '@/core/firebase';
import { scheduleWorkoutReminder } from '@/core/notifications';
import { Button, Card } from '../../../components/ui';
import type { EquipmentType, ExerciseEquipment } from '../../../types';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

export function BookIntroSession() {
  const { user, userData, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [booking, setBooking] = useState(false);

  // Derive workout parameters from onboarding data
  const workoutDuration = useMemo(() => {
    return userData?.preferredDuration as 30 | 45 | 60 | undefined;
  }, [userData?.preferredDuration]);

  const equipment = useMemo(() => {
    return (userData?.equipment?.length ? userData.equipment : ['none']) as EquipmentType[];
  }, [userData]);

  // Map user equipment to exercise equipment tags
  const equipmentTags = useMemo((): ExerciseEquipment[] => {
    const tagMap: Record<EquipmentType, ExerciseEquipment[]> = {
      'none': ['bodyweight'],
      'dumbbells': ['dumbbell', 'bodyweight'],
      'barbell': ['barbell', 'bodyweight'],
      'gym': ['bodyweight', 'dumbbell', 'barbell', 'cable', 'machine'],
      'resistance_bands': ['band', 'bodyweight'],
      'kettlebell': ['kettlebell', 'bodyweight'],
    };

    const tags = new Set<ExerciseEquipment>();
    equipment.forEach(eq => {
      const mapped = tagMap[eq];
      if (mapped) {
        mapped.forEach(tag => tags.add(tag));
      }
    });

    return Array.from(tags);
  }, [equipment]);

  // Generate next 7 days
  const availableDays = useMemo(() => {
    const days: Date[] = [];
    const today = new Date();
    
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    
    return days;
  }, []);

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const formatDate = (date: Date): string => {
    const dayName = DAY_NAMES[date.getDay()];
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${dayName}, ${month} ${day}`;
  };

  const handleBookSession = async () => {
    if (!user || !userData || !selectedDate || selectedTime === null) return;

    setBooking(true);
    try {
      // Create scheduled datetime
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(selectedTime, 0, 0, 0);

      console.log('[BookIntro] Calling bookIntroSession Cloud Function...');
      
      // Call Cloud Function to handle all booking operations server-side
      const bookIntroFn = httpsCallable(functions, 'bookIntroSession');
      const result = await bookIntroFn({
        scheduledFor: scheduledDateTime.toISOString(),
        equipmentTags,
        fitnessGoal: userData.fitnessGoal,
        sessionDuration: workoutDuration || 45,
      });

      const data = result.data as { success: boolean; templateId: string; assignmentId: string };
      console.log('[BookIntro] Booking successful:', data);

      await scheduleWorkoutReminder(scheduledDateTime, data.assignmentId, 'Intro Session');
      await refreshUserData();

      // Navigate to dashboard with success
      navigate('/client', { 
        state: { 
          message: 'Your intro session has been scheduled! We will send you a reminder.',
          fromOnboarding: true 
        } 
      });
    } catch (error) {
      console.error('Error booking session:', error);
      alert('Failed to book session. Please try again.');
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl" padding="lg" variant="elevated">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="mb-4 flex justify-center text-primary-600 dark:text-primary-400">
              <CalendarBlank size={40} weight="bold" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Book Your Free Intro Session
            </h1>
            <p className="text-gray-600 dark:text-slate-400">
              Choose a time for your first workout with Coach Fit
            </p>
          </div>

          {/* Day Selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
              Select a day
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {availableDays.map((day, index) => {
                const isSelected = selectedDate?.toDateString() === day.toDateString();
                return (
                  <button
                    key={index}
                    data-testid={`book-intro-day-${index}`}
                    onClick={() => {
                      setSelectedDate(day);
                      setSelectedTime(null); // Reset time when changing day
                    }}
                    className={`
                      p-4 rounded-xl border-2 transition-all text-center
                      ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                      }
                    `}
                  >
                    <div className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                      {DAY_NAMES[day.getDay()]}
                    </div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Selection */}
          {selectedDate && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                Select a time on {formatDate(selectedDate)}
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {HOURS.map((hour) => {
                  const isSelected = selectedTime === hour;
                  return (
                    <button
                      key={hour}
                      data-testid={`book-intro-time-${hour}`}
                      onClick={() => setSelectedTime(hour)}
                      className={`
                        px-3 py-2 rounded-lg text-sm font-medium transition-all
                        ${
                          isSelected
                            ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                            : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                        }
                      `}
                    >
                      {formatHour(hour)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm">
                <p className="text-blue-900 dark:text-blue-300 font-medium mb-1">
                  What to expect
                </p>
                <ul className="text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>Your first workout will be tailored to your experience level</li>
                  <li>We'll send you phone call reminders before your session</li>
                  <li>You can reschedule anytime from your dashboard</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Selected Time Summary */}
          {selectedDate && selectedTime !== null && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-900/30 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg
                  className="w-6 h-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm text-primary-900 dark:text-primary-300 font-medium">
                    Selected time
                  </p>
                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                    {formatDate(selectedDate)} at {formatHour(selectedTime)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4">
            <Button
              variant="primary"
              data-testid="book-intro-submit-button"
              onClick={handleBookSession}
              disabled={!selectedDate || selectedTime === null || booking}
              isLoading={booking}
              className="w-full"
            >
              {booking ? 'Booking...' : 'Book Session'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
